import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, RouterProps, withRouter } from 'react-router';
import * as checkoutActions from '../../actions/checkout';
import { Facility, FacilityFeature, IFacility } from '../../models/Facility';
import { IAppState } from '../../reducers';
import { Service as ServiceType } from '../../reducers/checkout';
import { getDefaultRate, getGlobalRate, getInternationalRate, getOutOfStateRate } from '../../utils/rateUtils';
import ErrorMessage from '../ErrorMessage';
import NameAndAddress from '../Facility/NameAndAddress';
import { ButtonSize, renderActionSubmit } from '../Form/RenderForm';
import Currency from '../Format/Currency';
import PageTitle from '../PageTitle';
import Navigation from './Navigation';

export enum CheckoutStep {
  Facility,
  Service,
  Checkout
}

function facilityHasService(facility: IFacility, service: ServiceType): boolean {
  if (facility.getConfigurationValue('FacilityOfflineMessage')) {
    return false;
  }

  switch (service) {
    case ServiceType.Wallet:
      return true;

    case ServiceType.AudioVideo:
      return (
        facility.features.indexOf(FacilityFeature.KioskAudioCalls) > -1 ||
        facility.features.indexOf(FacilityFeature.WirelessAudioCalls) > -1 ||
        facility.features.indexOf(FacilityFeature.WirelessVideoCalls) > -1
      );

    case ServiceType.Visitation:
      return (
        facility.visitationSchedulingEnabled
      )

    case ServiceType.PhoneCard:
      return facility.phoneCardsEnabled;

    case ServiceType.Collect:
      return facility.features.indexOf(FacilityFeature.PrepaidCollectCalls) > -1;

    case ServiceType.MailConnect:
      return facility.features.indexOf(FacilityFeature.MailConnect) > -1;
  }

  return false;
}

const Service: React.FC<RouterProps> = props => {
  const state = useSelector((appState: IAppState) => ({...appState.checkout}));
  const dispatch = useDispatch();
  const [expandedService, expandService] = useState<ServiceType>(null);
  const [message, setMessage] = useState<string>(null);

  if (state.common.facility == null) {
    return <Redirect to="/" />
  }

  useEffect(() => {
    if (state.common.facility?.getConfigurationValue('FacilityOfflineMessage')) {
      setMessage(state.common.facility.getConfigurationValue('FacilityOfflineMessage'));
    }
  }, [state.common.facility]);

  if (state.common.service !== null && state.common.facility?.getConfigurationValue('FacilityOfflineMessage') == null) {
    switch (state.common.service) {
      case ServiceType.Wallet:
        // Make sure facility supports wallet payments before redirect
        if (facilityHasService(state.common.facility, ServiceType.Wallet)) {
          props.history.push('/checkout/wallet');
          return null;
        }

      case ServiceType.Visitation:
        // Make sure facility supports visitations before redirect
        if (facilityHasService(state.common.facility, ServiceType.Visitation)) {
          props.history.push('/visitation/schedule');
          return null;
        }

      case ServiceType.PhoneCard:
        props.history.push('/checkout/card');
        return null;

      case ServiceType.Collect:
        // Make sure facility supports prepaid collect call payments before redirect
        if (facilityHasService(state.common.facility, ServiceType.Collect)) {
          props.history.push('/checkout/collect');
        }
        return null;
    }
  }

  return (
    <>
      <PageTitle title="Select Service" />
      <Navigation step={CheckoutStep.Service} onResetService={() => dispatch(checkoutActions.selectService(null))} />

      <main className="container align-items-top justify-content-center mt-3">
        <section className="no-gutters">
          <div className="main row">
            <div className="col-md-4 mb-3 rt-status">
              <aside className="bg-secondary text-light rounded p-3 p-lg-5">
                <h4>You've selected:</h4>
                <h5>Facility:</h5>
                <NameAndAddress facility={new Facility(state.common.facility)} />
              </aside>
            </div>

            <div className="col-md-8">
              {message &&
                <ErrorMessage
                  severity="warning"
                  message={message} />
              }

              {facilityHasService(state.common.facility, ServiceType.Wallet) &&
                <Accordion
                  className="bg-info text-light rounded inmatewallet service pt-lg-5 mb-3"
                  onSelect={() => expandService(ServiceType.Wallet)}>
                  <Accordion.Toggle
                    as={Button}
                    eventKey={ServiceType.Wallet.toString()}
                    className={`collapsible btn-info text-light btn-block px-lg-5 px-3 py-3 m-0 text-left rounded mobile-only ${expandedService == ServiceType.Wallet ? 'active' : ''}`}>
                    <h3 className="product m-0 pr-3"><span className="icon-button"></span>Inmate Wallet - All Options</h3><span className="collapseicon"></span>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey={ServiceType.Wallet.toString()} className="collapsecontent d-lg-block no-gutters">
                    <div>
                      <h3 className="product title-desktop"><span className="icon-product"></span>Inmate Wallet - All Options</h3>
                      <div className="bodysm">
                        <p>The funds paid into an <b>Inmate Wallet - All Options</b> account are tied directly to an inmate's personal device for them to use for texting, voice and video calls, and games, music and entertainment (where available). At most facilities, you can call the inmate directly. This account allows the inmate to communicate with multiple phone numbers. These funds are non-transferable and non-refundable. Credit card and transaction fees apply.</p>

                        <div className="product-details">
                          <span className="product-icon" />

                          <h6 className="subtitle subone">Texting Costs</h6>
                          <p>$0.09/message sent or received</p>

                          {getDefaultRate(state.common.facility, ServiceType.Wallet) &&
                            <>
                              <h6 className="subtitle subone">Phone Call Costs</h6>
                              <p><b>In State - </b><Currency value={getDefaultRate(state.common.facility, ServiceType.Wallet)} />/minute + applicable taxes</p>
                              <p><b>State to State - </b><Currency value={getDefaultRate(state.common.facility, ServiceType.Wallet)} />/minute + applicable taxes + Federal Universal Service Fund + Telecommunications Relay Service Fund</p>
                              <p><b>International - </b><Currency value={getInternationalRate(state.common.facility, ServiceType.Wallet)} />/minute + applicable taxes + Federal Universal Service Fund + Telecommunications Relay Service Fund</p>
                            </>
                          }
                          {(
                            state.common.facility.features.indexOf(FacilityFeature.WirelessVideoCalls) > -1 ||
                            state.common.facility.features.indexOf(FacilityFeature.VideoCalls) > -1
                          ) &&
                            <>
                              <h6 className="subtitle subone">Video Call Costs</h6>
                              <p><Currency value={getGlobalRate(state.common.facility, ServiceType.Wallet, 'video-call')} />/minute + applicable taxes</p>
                            </>
                          }
                        </div>
                      </div>

                      <div className="form-button-action">
                        {renderActionSubmit(null, 'Fund Inmate Wallet - All Options', 'Fund Inmate Wallet - All Options', ButtonSize.Large, true, () => {
                            dispatch(checkoutActions.selectService(ServiceType.Wallet));
                            props.history.push('/checkout/wallet');
                          }
                        )}
                      </div>
                    </div>
                  </Accordion.Collapse>
                </Accordion>
              }

              <div className="rounded service pt-md-2 row no-gutters">
                {facilityHasService(state.common.facility, ServiceType.AudioVideo) &&
                  <Accordion
                    className="col-lg-6 border border-midgray phonecard-gray rounded p-lg-2 pt-lg-5 mb-3"
                    onSelect={() => expandService(ServiceType.AudioVideo)}>
                    <Accordion.Toggle
                      as={Button}
                      variant="light"
                      eventKey={ServiceType.AudioVideo.toString()}
                      className={`collapsible btn-info text-secondary btn-block px-lg-5 px-3 py-3 m-0 text-left rounded mobile-only ${expandedService == ServiceType.AudioVideo ? 'active' : ''}`}>
                      <h3 className="product m-0 pr-3"><span className="icon-button"></span>Inmate Wallet - Voice & Video Calls Only</h3><span className="collapseicon_secondary"></span>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={ServiceType.AudioVideo.toString()} className="collapsecontent d-lg-block no-gutters">
                      <div>
                        <div className="bodysm">
                          <h3 className="product title-desktop text-dark mr-4"><span className="dashboard-tile-icon rt-phonecard"></span>Inmate Wallet - Voice & Video Calls Only</h3>
                          <div className="text-secondary">
                            <p>Funds paid into an <b>Inmate Wallet - Voice & Video Calls Only</b> account are tied directly to an inmate's personal device for them to use for voice and video calling <b>ONLY</b>. At most facilities, you can call the inmate directly. This account allows the inmate to call multiple phone numbers. No fees apply to the purchase, and it is refundable.</p>
                            <div className="product-details">
                              <span className="product-icon" />

                              {getDefaultRate(state.common.facility, ServiceType.Wallet) &&
                                <>
                                  <h6 className="subtitle subone">Phone Call Costs</h6>
                                  <p><b>In State - </b><Currency value={getDefaultRate(state.common.facility, ServiceType.Wallet)} />/minute + applicable taxes</p>
                                  <p><b>State to State - </b><Currency value={getDefaultRate(state.common.facility, ServiceType.Wallet)} />/minute + applicable taxes + Federal Universal Service Fund + Telecommunications Relay Service Fund</p>
                                  <p><b>International - </b><Currency value={getInternationalRate(state.common.facility, ServiceType.Wallet)} />/minute + applicable taxes + Federal Universal Service Fund + Telecommunications Relay Service Fund</p>
                                </>
                              }
                              {(
                                state.common.facility.features.indexOf(FacilityFeature.WirelessVideoCalls) > -1 ||
                                state.common.facility.features.indexOf(FacilityFeature.VideoCalls) > -1
                              ) &&
                                <>
                                  <h6 className="subtitle subone">Video Call Costs</h6>
                                  <p><Currency value={getGlobalRate(state.common.facility, ServiceType.Wallet, 'video-call')} />/minute + applicable taxes</p>
                                </>
                              }
                            </div>
                          </div>
                        </div>
                        <p>
                          <button
                            onClick={() => {
                              dispatch(checkoutActions.selectService(ServiceType.AudioVideo));
                              props.history.push('/checkout/audio-video');
                              return false;
                            }}
                            className="btn btn-block bg-action text-white">
                            Fund Inmate Wallet - Voice & Video Calls Only
                          </button>
                        </p>
                      </div>
                    </Accordion.Collapse>
                  </Accordion>
                }

                {facilityHasService(state.common.facility, ServiceType.Collect) &&
                  <Accordion
                    className="col-lg-6 border border-midgray collectcall-gray rounded p-lg-2 pt-lg-5 mb-3"
                    onSelect={() => expandService(ServiceType.Collect)}>
                    <Accordion.Toggle
                      as={Button}
                      variant="light"
                      eventKey={ServiceType.Collect.toString()}
                      className={`collapsible btn-info text-secondary btn-block px-lg-5 px-3 py-3 m-0 text-left rounded mobile-only ${expandedService == ServiceType.Collect ? 'active' : ''}`}>
                      <h3 className="product m-0 pr-3"><span className="icon-button"></span>Prepaid Collect Calls</h3><span className="collapseicon_secondary"></span>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={ServiceType.Collect.toString()} className="collapsecontent d-lg-block no-gutters">
                      <div>
                        <div className="bodysm">
                          <h3 className="product title-desktop text-dark mr-4"><span className="dashboard-tile-icon rt-phonecard"></span>Prepaid Collect Calls</h3>
                          <div className="text-secondary">
                            <p>Collect call accounts are on a prepayment basis and you must maintain a positive credit balance to keep your line open. Prepaid collect call accounts are set up with a specific telephone number. Funds in this account can only be used for calls to or from the specified telephone number. No fees apply to the purchase, and it is refundable.</p>
                            <div className="product-details">
                              <span></span>

                              {getDefaultRate(state.common.facility, ServiceType.Collect) &&
                                <>
                                  <h6 className="subtitle subone">In-State Calls</h6>
                                  <p><Currency value={getDefaultRate(state.common.facility, ServiceType.Collect)} />/minute + applicable taxes</p>
                                </>
                              }

                              {getOutOfStateRate(state.common.facility, ServiceType.Collect) &&
                                <>
                                  <h6 className="subtitle subone">State to State Calls</h6>
                                  <p><Currency value={getOutOfStateRate(state.common.facility, ServiceType.Collect)} />/minute + Federal Universal Service Fund + Telecommunications Relay Service Fund</p>
                                </>
                              }

                              {getInternationalRate(state.common.facility, ServiceType.Collect) &&
                                <>
                                  <h6 className="subtitle subone">International Calls</h6>
                                  <p><Currency value={getInternationalRate(state.common.facility, ServiceType.Collect)} />/minute + applicable taxes</p>
                                </>
                              }
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              dispatch(checkoutActions.selectService(ServiceType.Collect));
                              props.history.push('/checkout/collect');
                              return false;
                            }}
                            className="btn btn-block bg-action text-white">
                            Set up Collect Call Account
                          </button>
                        </div>
                      </div>
                    </Accordion.Collapse>
                  </Accordion>
                }
              </div>

              {facilityHasService(state.common.facility, ServiceType.Visitation) &&
                <div className="service pt-md-2 row no-gutters">
                  <Accordion
                    className="col-lg-12 border border-midgray visitation-gray rounded p-lg-2 pt-lg-5 mb-3"
                    onSelect={() => expandService(ServiceType.Visitation)}>
                    <Accordion.Toggle
                      as={Button}
                      variant="light"
                      eventKey={ServiceType.Visitation.toString()}
                      className={`collapsible btn-info text-secondary btn-block px-lg-5 px-3 py-3 m-0 text-left rounded mobile-only ${expandedService == ServiceType.Visitation ? 'active' : ''}`}>
                      <h3 className="product m-0 pr-3"><span className="icon-button"></span>Schedule a Visitation</h3><span className="collapseicon_secondary"></span>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={ServiceType.Visitation.toString()} className="collapsecontent d-lg-block no-gutters">
                      <div>
                        <div className="bodysm">
                          <h3 className="product title-desktop text-dark mr-4"><span className="dashboard-tile-icon rt-phonecard"></span>Schedule a Video Visitation at the Jail</h3>
                          <div className="text-secondary">
                            <p>Create an account, or log in to an existing account, to schedule a video visitation at the jail.</p>
                          </div>
                        </div>
                        <div>
                          <p>
                            <a
                              onClick={() => {
                                dispatch(checkoutActions.selectService(ServiceType.Visitation));
                                props.history.push('/visitation/schedule');
                                return false;
                              }}
                              className="btn btn-block bg-secondary text-white">
                              Schedule Video Visitation
                            </a>
                          </p>
                        </div>
                      </div>
                    </Accordion.Collapse>
                  </Accordion>
                </div>
              }

              {facilityHasService(state.common.facility, ServiceType.MailConnect) &&
                <div className="service pt-md-2 row no-gutters">
                  <Accordion
                    className="col-lg-12 border border-midgray postoffice-gray rounded p-lg-2 pt-lg-5 mb-3"
                    onSelect={() => expandService(ServiceType.MailConnect)}>
                    <Accordion.Toggle
                      as={Button}
                      variant="light"
                      eventKey={ServiceType.MailConnect.toString()}
                      className={`collapsible btn-info text-secondary btn-block px-lg-5 px-3 py-3 m-0 text-left rounded mobile-only ${expandedService == ServiceType.MailConnect ? 'active' : ''}`}>
                      <h3 className="product m-0 pr-3"><span className="icon-button"></span>Send Mail to an Inmate</h3><span className="collapseicon_secondary"></span>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={ServiceType.MailConnect.toString()} className="collapsecontent d-lg-block no-gutters">
                      <div>
                        <div className="bodysm">
                          <h3 className="product title-desktop text-dark mr-4"><span className="dashboard-tile-icon rt-mail"></span>Send Physical Mail to an Inmate</h3>
                          <div className="text-secondary">
                            <p>Create an account, or log in to an existing account, to send physical mail, letters, pictures, or documents to an inmate.</p>
                          </div>
                        </div>
                        <div>
                          <p>
                            <a
                              onClick={() => {
                                dispatch(checkoutActions.selectService(ServiceType.MailConnect));
                                props.history.push('/post-office/send');
                                return false;
                              }}
                              className="btn btn-block bg-secondary text-white">
                              Send Mail
                            </a>
                          </p>
                        </div>
                      </div>
                    </Accordion.Collapse>
                  </Accordion>
                </div>
              }
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default withRouter(Service);
